import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
const AuthContext = createContext();

// On startup, restore the JWT into the axios default header so every
// subsequent API call automatically authenticates.
const TOKEN_KEY = "app_token";
const initialToken = (() => {
  try {
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
})();
if (initialToken) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${initialToken}`;
}

// Helper to get user from storage with fallback
const getUserFromStorage = () => {
  try {
    // Try localStorage first
    let saved = localStorage.getItem("app_user");
    if (saved) {
      const parsed = JSON.parse(saved);
      // Also save to sessionStorage as backup
      sessionStorage.setItem("app_user_backup", saved);
      return parsed;
    }
    
    // Fallback to sessionStorage backup
    saved = sessionStorage.getItem("app_user_backup");
    if (saved) {
      const parsed = JSON.parse(saved);
      // Restore to localStorage
      localStorage.setItem("app_user", saved);
      return parsed;
    }
    
    return null;
  } catch {
    return null;
  }
};

export function AuthProvider({ children }) {
  // Initialize from localStorage with sessionStorage fallback
  const [user, setUser] = useState(() => getUserFromStorage());
  
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!getUserFromStorage();
  });

  // If user exists but doesn't have business_category, fetch it from server
  useEffect(() => {
    const refreshUserData = async () => {
      if (user && user.business_slug && !user.business_category) {
        try {
          // Get business info to get the category
          const response = await axios.get(`${API}/b/${user.business_slug}/info`);
          if (response.data?.category) {
            const updatedUser = { ...user, business_category: response.data.category };
            setUser(updatedUser);
            localStorage.setItem("app_user", JSON.stringify(updatedUser));
          }
        } catch (error) {
          console.error("Failed to refresh user data:", error);
        }
      }
    };
    refreshUserData();
  }, [user?.business_slug]);

  // Listen for storage changes (in case of multiple tabs)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "app_user") {
        if (e.newValue) {
          try {
            setUser(JSON.parse(e.newValue));
            setIsAuthenticated(true);
          } catch {
            // Invalid data, ignore
          }
        } else {
          // Only logout if storage was explicitly cleared
          setUser(null);
          setIsAuthenticated(false);
        }
      }
    };
    
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const login = (userData) => {
    setUser(userData);
    const userJson = JSON.stringify(userData);
    localStorage.setItem("app_user", userJson);
    sessionStorage.setItem("app_user_backup", userJson); // Backup
    // Persist JWT and set the axios default header so all subsequent
    // requests are authenticated automatically.
    if (userData?.access_token) {
      localStorage.setItem(TOKEN_KEY, userData.access_token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${userData.access_token}`;
    }
    setIsAuthenticated(true);
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem("app_user");
    sessionStorage.removeItem("app_user_backup"); // Clear backup too
    localStorage.removeItem(TOKEN_KEY);
    delete axios.defaults.headers.common["Authorization"];
  };

  // Helper to check if user is super admin
  const isSuperAdmin = user?.role === "super_admin";
  
  // Get the business slug for API calls
  const businessSlug = user?.business_slug;
  
  // Get the business category for routing
  const businessCategory = user?.business_category;

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      login, 
      logout,
      isSuperAdmin,
      businessSlug,
      businessCategory
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
